import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { DropAction } from './groupLayoutUtils'

export type RejectionReason = 'category_mismatch' | 'unsupported' | null

export interface DragOutState {
  sourceGroupId: string
  distanceFactor: number  // 0 = at boundary, 1 = at threshold
  nodeName: string
  groupName: string
}

export interface RemovedFlashState {
  sourceGroupId: string
  nodeName: string
  groupName: string
}

export interface DropTargetState {
  targetGroupId: string | null
  insertionIndex: number
  dropAction: DropAction
  rejection: RejectionReason
  dragOutState?: DragOutState | null
  removedFlash?: RemovedFlashState | null
}

export interface GroupDropTargetContextValue {
  dropTarget: DropTargetState
  setDropTarget: (state: DropTargetState) => void
  clearDropTarget: () => void
  setRemovedFlash: (flash: RemovedFlashState | null) => void
  activeDragNodeId: string | null
  setActiveDragNodeId: (id: string | null) => void
}

const GroupDropTargetContext = createContext<GroupDropTargetContextValue | null>(null)

export function useGroupDropTarget(): GroupDropTargetContextValue | null {
  return useContext(GroupDropTargetContext)
}

export function GroupDropTargetProvider({ children }: { children: React.ReactNode }) {
  const [dropTarget, setDropTargetState] = useState<DropTargetState>({
    targetGroupId: null,
    insertionIndex: 0,
    dropAction: 'insert-before',
    rejection: null,
    dragOutState: null,
    removedFlash: null,
  })

  const setDropTarget = useCallback((state: DropTargetState) => {
    setDropTargetState(state)
  }, [])

  const clearDropTarget = useCallback(() => {
    setDropTargetState((prev) => ({
      targetGroupId: null,
      insertionIndex: 0,
      dropAction: 'insert-before' as DropAction,
      rejection: null,
      dragOutState: null,
      removedFlash: prev.removedFlash,  // Preserve flash state
    }))
  }, [])

  const setRemovedFlash = useCallback((flash: RemovedFlashState | null) => {
    setDropTargetState((prev) => ({ ...prev, removedFlash: flash }))
  }, [])

  const [activeDragNodeId, setActiveDragNodeIdState] = useState<string | null>(null)

  const setActiveDragNodeId = useCallback((id: string | null) => {
    setActiveDragNodeIdState(id)
  }, [])

  const value = useMemo(
    () => ({ dropTarget, setDropTarget, clearDropTarget, setRemovedFlash, activeDragNodeId, setActiveDragNodeId }),
    [dropTarget, setDropTarget, clearDropTarget, setRemovedFlash, activeDragNodeId, setActiveDragNodeId],
  )

  return (
    <GroupDropTargetContext.Provider value={value}>
      {children}
    </GroupDropTargetContext.Provider>
  )
}
